<template>
  <div class="records-history" :class="{ 'records-history-mobile': isMobile }">
    <div class="records-history__header">
      <div
        class="records-history__block records-history__header-wrapper"
        :class="{ 'records-history__header-mobile': isMobile }"
      >
        <!--Sidebar menu icon on mobile-->
        <v-btn
          flat
          icon
          small
          v-if="isMobile && !navigationDrawer"
          class="records-history__sidebar-icon"
          @click="mobileChangeSidebarVisible"
        >
          <SvgIcon height="24" width="24" icon="Hamburger" />
        </v-btn>
        <!-- End sidebar menu icon on mobile-->

        <div class="records-history__header__title">{{ $t('page.booking') }}</div>

        <div class="records__tool-filter">
          <v-btn
            v-if="!isMobile"
            class="records-refresh__btn"
            depressed
            color="grey lighten-3"
            @click="refreshData"
          >
            <SvgIcon class="tag-icon" height="20" width="20" icon="Reuse" />
            <span>{{ $t('partner.refresh') }}</span>
          </v-btn>

          <RecordsTimeRange
            v-if="!isMobile"
            :activatorLabel="timeRangeLabel"
            :initOption="params.time"
            :initFromDate="params.from_time"
            :initToDate="params.to_time"
            @onChange="handleChangeDatePicker"
          />
          <RecordsEmployees
            v-if="!isMobile && showEmployeesSelection"
            :selectedEmployees="params.child_ids"
            :employees="employees"
            @onChange="handleSelectedEmployees"
          />
          <RecordsServices
            v-if="!isMobile"
            :selectedServices="params.service_ids"
            :services="serviceTypes"
            @onChange="handleSelectedServices"
          />

          <!-- Records filter tools's activator on mobile -->
          <v-btn
            v-if="isMobile"
            depressed
            color="grey lighten-3"
            class="records-history__mobile-filter-btn filter-search"
            @click="handleClickMobileFilter('search')"
          >
            <SvgIcon height="24" width="24" icon="SearchOutline" />
          </v-btn>
        </div>
      </div>
      <div class="records-history-tabs">
        <Tabs
          :tabs="status"
          :metaData="orderMeta"
          :activeTab="params.statusIndex"
          :isShowCount="false"
          @onClick="changeStatusFilter"
        />
      </div>
    </div>
    <div class="records-history__body">
      <div class="records-history__card">
        <!-- Records filter's tools -->
        <div class="records-history__body__tool" v-if="!isMobile || showFilterOrSearchOnMobile">
          <SearchBox
            v-if="!isMobile || showFilterOrSearchOnMobile === 'search'"
            ref="searchBox"
            :disabled="isLoadingBookingList || haveSearchByServiceIds"
            @onReset="handleResetSearch"
            @onChange="handleSearch"
          />
        </div>

        <!-- Records list wrapper -->
        <div v-if="!isLoadingBookingList" class="records-history__body__list">
          <!-- No records -->
          <div v-if="showNoOrderMessage" class="records-history__body__list-empty">
            <span v-if="params.status && params.status !== 'ALL'">
              {{ $t('records.no_orders_in_status', { status: paramsStatusLabel }) }}
            </span>
            <span v-else>{{ $t('records.no_result') }}</span>
          </div>

          <!-- No search result -->
          <div v-else-if="noSearchResult" class="records-history__body__list-no-result">
            <img src="/assets/images/NoSearchResult.png" alt="no-search-result" />
            <span>{{ $t('records.no_search_result', { search_text: searchText }) }}</span>
          </div>

          <!-- Records list -->
          <div v-else class="records-history__body__list-data">
            <CardsView v-if="isMobile" employee :orders="orders" :loading="isLoadingBookingList" />

            <ListView
              v-else
              employee
              :orders="orders"
              :searchText="params.term"
              :loading="isLoadingBookingList"
            />
          </div>

          <div class="records-history__body__list__footer">
            <div class="records-pagination">
              <div
                class="records-pagination-info"
                v-if="!isLoadingBookingList && orders && orders.length > 0"
              >
                <v-menu
                  offset-y
                  nudge-bottom="4"
                  content-class="records-pagination-options"
                  :min-width="100"
                >
                  <span slot="activator" class="records-pagination-range">{{
                    paginationTitle
                  }}</span>
                  <v-list>
                    <span>{{ $t('table_rows_per_page') }}</span>
                    <v-list-tile class="th-cell-option" @click="handleSelectPaginationOffset(10)"
                      >10</v-list-tile
                    >
                    <v-list-tile class="th-cell-option" @click="handleSelectPaginationOffset(15)"
                      >15</v-list-tile
                    >
                    <v-list-tile class="th-cell-option" @click="handleSelectPaginationOffset(20)"
                      >20</v-list-tile
                    >
                  </v-list>
                </v-menu>
                <span>&nbsp;{{ paginationTotalTitle }}</span>
              </div>
              <div class="records-pagination-actions" v-if="orders && orders.length > 0">
                <v-btn
                  :disabled="params.page === 1 || isLoadingBookingList"
                  @click="onChangePage('previous')"
                >
                  <v-icon color="#0000008a">chevron_left</v-icon>
                </v-btn>
                <v-btn
                  :disabled="exceedMaxPage || isLoadingBookingList"
                  @click="onChangePage('next')"
                >
                  <v-icon color="#0000008a">chevron_right</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading indicator -->
        <div v-else class="records-history__loading">
          <v-progress-circular indeterminate color="#FF8200" :width="2" :size="20" />
          <span>{{ $t('loading') }}</span>
        </div>
      </div>
    </div>

    <v-dialog
      light
      scrollable
      v-if="activeOrderData"
      v-model="activeOrderData"
      :transition="false"
      lazy
      :width="packageDetailSize.width"
      :height="packageDetailSize.height"
    >
      <RecordDetail
        :data="activeOrderData"
        @onClose="activeOrderData = null"
        @onCancelOrder="handleCancelOrder"
        @onShowReuseOrder="handleShowReuseOrder"
      />
    </v-dialog>

    <v-dialog
      light
      scrollable
      v-if="isShowReuseOrder"
      v-model="isShowReuseOrder"
      :transition="false"
      lazy
      :width="packageDetailSize.reUseOrderWidth"
    >
      <OnWheelReuseOrder
        v-if="isBigC"
        :order="reUserOrderData"
        @onShowReuseOrder="handleShowReuseOrder"
        @onReuseOrderSuccess="handleReuseOrderSuccess"
        @onClose="isShowReuseOrder = false"
      />
    </v-dialog>

    <Permission :showDialog="showDialogPermission"></Permission>
  </div>
</template>

<i18n>
  {
    "en": {
      "table_rows_per_page": "Rows per page",
      "pagination_total_rows": "rows",
      "pagination_of_total": "of total"
    },
    "vi": {
      "table_rows_per_page": "Số đơn hiển thị",
      "pagination_total_rows": "đơn",
      "pagination_of_total": "trên tổng đơn"
    }
  }
</i18n>

<script>
import { mapGetters, mapState } from 'vuex'
import i18next from '@/plugins/i18next'
import NS_records_vi from '@/locales2/vi/records.json'
import NS_records_en from '@/locales2/en/records.json'

i18next.addResourceBundle('vi', 'common', NS_records_vi, true)
i18next.addResourceBundle('en', 'common', NS_records_en, true)

import Tabs from '@/partner/components/records/Tabs'
import ListView from '@/partner/components/records/ListView'
const CardsView = () => import('@/partner/components/records/CardsView')
const SearchBox = () => import('@/partner/components/records/list_view/SearchBox')
const Permission = () => import('@/onwheel/components/sgc/Permission')
const OnWheelReuseOrder = () =>
  import('@/onwheel/components/record_detail/reuse_order/ReuseOrder.vue')
import RecordsTimeRange from '@/partner/components/records/filter/RecordsTimeRange'
import RecordsEmployees from '@/components/records_v2/RecordsEmployees'
import RecordsServices from '@/components/records_v2/RecordsServices'

import RecordDetail from '@/partner/components/record_detail/RecordDetail'

import record from '@/mixins/record'
import { serviceDetail } from '@/store/api/order'
import PreventScrollMixin from '@/mixins/prevent-scroll'

import { searchRecordListOnwheel, getOrderMeta } from '@/store/api/order'
import { getServiceDetail, getFromCookieSafe } from '@/plugins/util'

const asyncQueue = require('async/queue')

const {
  omit,
  get,
  cloneDeep
  // findIndex
} = require('@/plugins/lodash')

const PAGE_COUNT = 15
// const REQUEST_TIMEOUT = 5000;

export default {
  components: {
    Tabs,
    ListView,
    CardsView,
    SearchBox,
    RecordsTimeRange,
    RecordDetail,
    OnWheelReuseOrder,
    Permission,
    RecordsEmployees,
    RecordsServices
  },
  mixins: [record, PreventScrollMixin],
  data() {
    return {
      params: {
        time:
          getFromCookieSafe(
            JSON.parse(this.$cookie.get(this.$constants.COOKIE_NAME__SELECTED_TIME_PICKER) || '{}'),
            'time'
          ) || 'TODAY',
        status: null,
        from_time:
          getFromCookieSafe(
            JSON.parse(this.$cookie.get(this.$constants.COOKIE_NAME__SELECTED_TIME_PICKER) || '{}'),
            'from_time'
          ) || null,
        to_time:
          getFromCookieSafe(
            JSON.parse(this.$cookie.get(this.$constants.COOKIE_NAME__SELECTED_TIME_PICKER) || '{}'),
            'to_time'
          ) || null,
        statusIndex: 0,
        page: 1,
        count: 15,
        child_ids: [],
        service_ids: []
      },

      isLoadingBookingList: false,

      orders: [],
      orderMeta: [],

      searchMode: false,
      searchText: '',
      searchOption: null,

      // Show tools: filter/search on mobile
      showFilterOrSearchOnMobile: '',
      intervalId: null,
      activeOrderData: undefined,

      showDialogPermission: false,

      isShowReuseOrder: false,
      reUserOrderData: {}
    }
  },
  computed: {
    ...mapGetters(['activeOrder', 'serviceTypes']),
    ...mapState({
      userProfile: (state) => state.user.profile
    }),
    haveSearchByServiceIds() {
      return this.params.service_ids.length > 0
    },
    isBigC() {
      return (
        this.userProfile &&
        this.userProfile.partner &&
        this.userProfile.partner.toLowerCase() === this.$constants.HARDCODED_PARTNER.BIGC
      )
    },
    allowCancelOrderFromKA() {
      const kaProfile = this.$store.getters.kaProfile
      return get(kaProfile, 'ro_settings.cancel_order_ahamove') || false
    },
    status() {
      let status = []
      const filterStatus = omit(this.$constants.ORDER_STATUS, ['FAILED'])
      const blurStatus = [
        this.$constants.ORDER_STATUS.COMPLETED,
        this.$constants.ORDER_STATUS.CANCELLED,
        'ALL'
      ]

      for (let key in filterStatus) {
        status.push({
          value: this.$constants.ORDER_STATUS[key],
          text: this.$t('order_status.' + key),
          blur: blurStatus.includes(key)
        })
      }

      return status
    },
    noSearchResult() {
      return this.searchMode && this.orders.length === 0 && !this.isLoadingBookingList
    },
    showNoOrderMessage() {
      return this.orders.length === 0 && !this.searchMode && !this.isLoadingBookingList
    },
    services() {
      return this.$store.getters.serviceTypes
    },
    localeSuffix() {
      return this.$store.getters.localeSuffix
    },
    navigationDrawer() {
      return this.$store.getters.navigationDrawer
    },
    paramsStatusLabel() {
      const statusLabel =
        this.params &&
        this.params.status &&
        this.$t('order_status.' + this.params.status.replace(' ', '_'))
      return statusLabel || ''
    },
    employees() {
      return this.$store.getters.employees
    },
    showEmployeesSelection() {
      return this.employees.length > 0
    },
    timeRangeLabel() {
      if (this.params.time === 'CUSTOM') {
        if (this.params.from_time && this.params.to_time) {
          return `${this.$moment.unix(this.params.from_time).format('DD/MM/YYYY')} - ${this.$moment
            .unix(this.params.to_time)
            .format('DD/MM/YYYY')}`
        }

        return ''
      } else if (this.params.time === 'ALL') return ''
      else if (this.params.time) {
        return this.$t(`date_range_picker.${this.params.time.toLowerCase()}`)
      } else return ''
    },
    paginationTitle() {
      const from = (this.params.page - 1) * this.params.count + 1

      let to = this.params.count * this.params.page

      if (this.orders && this.exceedMaxPage) {
        if (this.params.page === 1) {
          to = this.orders.length
        } else {
          to = from + this.orders.length - 1
        }
      }

      return `${from}-${to}`
    },
    paginationTotalTitle() {
      return this.params.page === 1 && this.exceedMaxPage
        ? this.$t('pagination_total_rows')
        : this.$t('pagination_of_total')
    },
    exceedMaxPage() {
      return !this.orders || this.orders.length < this.params.count
    },
    packageDetailSize() {
      const screenWidth =
        (this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.width) ||
        (window && window.innerWidth) ||
        1200

      const screenHeight =
        (this.$vuetify && this.$vuetify.breakpoint && this.$vuetify.breakpoint.height) ||
        (window && window.innerHeight) ||
        900

      return {
        width: screenWidth - 400,
        height: screenHeight - 300,
        reUseOrderWidth: screenWidth - 500
      }
    }
  },
  methods: {
    async $_checkAnCuongRecordsV2Permission() {
      try {
        if (!this.$store.getters.userProfile) {
          await this.$store.dispatch('getUserProfile')
        }
        if (!this.$store.getters.kaProfile) {
          await this.$store.dispatch('getKeyAccountProfile')
        }
      } catch (e) {
        return
      }
      const userProfile = this.$store.getters.userProfile || {}
      if (userProfile.partner !== this.$constants.HARDCODED_PARTNER.ANCUONG) return
      const kaProfile = this.$store.getters.kaProfile || {}
      const whiteList = get(kaProfile, 'superadmin_whitelist', [])
      const isSuperAdminAccount = whiteList.includes(userProfile._id)
      const saasPermissions = get(userProfile, 'saas_permissions', [])
      const hasRecordsV2Permission = saasPermissions.some(
        (p) => p === 'records-v2' || p === 'records_v2'
      )
      if (!isSuperAdminAccount && !hasRecordsV2Permission) {
        this.$router.replace('/profile')
      }
    },
    async getOrdersMeta() {
      const response = await getOrderMeta()
      const { data } = response
      this.orderMeta = data
    },
    buildRequestConfig() {
      const date = {
        from_time: this.params.from_time,
        to_time: this.params.to_time
      }
      const count = this.offset === 0 ? PAGE_COUNT * 2 : PAGE_COUNT
      let payload = {
        total: true,
        offset: (this.params.page - 1) * count,
        count,
        token: this.$store.getters.token
      }

      if (this.params.term) {
        payload.term = this.params.term
      }

      if (this.params.child_ids && this.params.child_ids.length > 0) {
        let ids = ''
        this.params.child_ids.forEach((employee, index) => {
          if (index === 0) ids = employee
          else ids += `,${employee}`
        })

        payload.child_ids = ids
      }

      if (this.params.tags) {
        let tagsString = ''

        this.params.tags.forEach((tag, index) => {
          if (index === 0) tagsString = tag
          else tagsString += `,${tag}`
        })

        payload.tags = tagsString
      }

      if (this.params.service_ids) {
        let service_idsString = ''

        this.params.service_ids.forEach((service_id, index) => {
          if (index === 0) service_idsString = service_id
          else service_idsString += `,${service_id}`
        })

        payload.service_ids = service_idsString
      }

      if (
        this.userProfile &&
        this.userProfile.saas_config &&
        this.userProfile.saas_config.stores &&
        this.userProfile.saas_config.stores.length > 0 &&
        this.userProfile.saas_config.stores[0].store_id
      ) {
        payload.store_id = this.userProfile.saas_config.stores[0].store_id
      }
      if (
        this.userProfile &&
        this.userProfile.saas_config &&
        this.userProfile.saas_config.store &&
        this.userProfile.saas_config.store.store_id
      ) {
        payload.store_id = this.userProfile.saas_config.store.store_id
      }

      let params = Object.assign(payload, date)
      if (this.params.status !== this.$constants.ORDER_STATUS.ALL)
        params.status = this.params.status
      if (this.$_PAGE === this.$constants.PAGE.TRUCK) params.service = 'TRUCK'
      return params
    },
    getBookingList() {
      if (this.isLoadingBookingList) return

      const requestConfig = this.buildRequestConfig()

      // if (this.params.status) {
      //   params.status = this.params.status
      // }

      // if (this.searchMode) {
      //   params[this.searchOption] = this.searchText
      // }

      this.isLoadingBookingList = true

      searchRecordListOnwheel(requestConfig)
        .then((response) => {
          if (response) {
            const orders = response.orders || []

            // if (response.meta) {
            //   this.orderMeta = response.meta
            // }

            this.updateOrderInfo(orders).then((updatedOrders) => {
              // if (updatedOrders.length > 0) {
              this.orders = updatedOrders
              let map = new Map()

              this.orders.forEach((order) => {
                if (map.get(`${order.status}`)) {
                  map.set(`${order.status}`, map.get(`${order.status}`) + 1)
                } else {
                  map.set(`${order.status}`, 1)
                }
              })
              this.orderMeta = []
              for (const [key, value] of map) {
                this.orderMeta.push({ _id: key, count: value })
              }
              // }
            })
          }

          this.isLoadingBookingList = false
          this.$store.dispatch('setOrders', this.orders)

          this.$analyticTracking.logEvent(
            this.$trackingEvents.ACTIONS.CLICK,
            this.$trackingEvents.EVENTS.PARTNER_LOAD_BOOKING_LIST,
            {}
          )
        })
        .catch((error) => {
          this.isLoadingBookingList = false
          this.tableData = []
          if (error.response) {
            this.$_globalMixin_requestErrorHandle(error.response)
          }
        })
    },
    refreshData() {
      this.params = { ...this.params, page: 1 }

      this.getBookingList()
    },
    handleSelectedEmployees(selectedEmployees) {
      this.params.child_ids = selectedEmployees
      this.params.page = 1
      this.refreshData()
      this.$forceUpdate()
    },
    handleSelectedServices(selectedServices) {
      this.params.service_ids = selectedServices
      this.params.page = 1
      this.refreshData()
      this.$forceUpdate()
    },
    updateOrderInfo(orders) {
      return new Promise((resolve) => {
        const updatedOrders = []
        let services = []
        if (this.services && this.services.length > 0) {
          services = [...this.services]
        }

        const handleTaskInQueue = async (order, cb) => {
          let service = getServiceDetail(services, order.service_id)

          if (!service) {
            const response = await serviceDetail({
              service_id: order.service_id
            })

            if (response.status === 200) {
              service = response.data
              services.push(service)
            }
          }
          order.service_name = service && (service['name' + this.localeSuffix] || service.name)
          order.service = service
          let totalCOD = 0

          order.path.forEach((p) => {
            totalCOD += p.cod || 0
          })

          order.total_cod = totalCOD

          updatedOrders.push({ ...order })
          cb()
        }

        const updateServiceInfoQueue = asyncQueue(function (task, cb) {
          handleTaskInQueue(task, cb)
        }, 1)

        updateServiceInfoQueue.push(orders, function (err) {
          if (err) {
            console.error('updateServiceInfoQueue', err)
          }
        })

        updateServiceInfoQueue.drain = () => {
          // update services store
          if (services.length !== this.services.length) {
            this.$store.dispatch('setServiceTypes', services)
          }

          resolve(updatedOrders)
        }
      })
    },
    mobileChangeSidebarVisible() {
      const value = !this.navigationDrawer
      this.$store.dispatch('setNavigationDrawer', value)
    },
    handleClickMobileFilter(tool) {
      if (this.showFilterOrSearchOnMobile === tool) {
        this.showFilterOrSearchOnMobile = ''
      } else {
        this.showFilterOrSearchOnMobile = tool
      }
    },
    handleResetSearch() {
      this.params.term = ''

      this.getBookingList()
    },
    handleSearch(text) {
      console.log(text)
      // this.searchMode = true
      // this.searchText = text
      // this.searchOption = option
      this.params.term = text

      this.orders = []
      this.getBookingList()
    },
    changeStatusFilter(index) {
      this.params.statusIndex = index
      if (this.status[index].value === this.$constants.ORDER_STATUS.ALL) {
        this.params.status = null
      } else {
        this.params.status = this.status[index].value
      }

      this.params = { ...this.params, page: 1 }
      this.orders = []
      this.handleResetSearch()
    },
    handleChangeDatePicker(payload) {
      this.params.from_time = payload.from_time
      this.params.to_time = payload.to_time
      this.params.time = payload.option

      this.params = { ...this.params, page: 1 }

      this.orders = []
      this.getBookingList()

      this.$cookie.set(
        this.$constants.COOKIE_NAME__SELECTED_TIME_PICKER,
        JSON.stringify(
          payload.option === 'CUSTOM'
            ? {
                time: payload.option,
                from_time: payload.from_time,
                to_time: payload.to_time
              }
            : {
                time: payload.option
              }
        ),
        {
          expires: this.$constants.COOKIE_EXPIRES_MAX_AGE__3M
        }
      )
    },
    handleSelectPaginationOffset(value) {
      this.params.count = value
    },
    onChangePage(action) {
      if (action === 'next') {
        if (!this.exceedMaxPage) {
          this.params.page += 1
        }
      } else {
        if (this.params.page > 1) {
          this.params.page -= 1
        }
      }

      this.getBookingList()
    },
    handleCancelOrder() {
      if (this.isBigC || this.allowCancelOrderFromKA) {
        this.refreshData()
        this.activeOrderData.status = this.$constants.ORDER_STATUS.CANCELLED
        this.$store.dispatch('setActiveOrder', this.activeOrderData)
      }
    },
    handleShowReuseOrder() {
      this.reUserOrderData = cloneDeep(this.activeOrderData) || {}

      // show reuse order dialog
      this.isShowReuseOrder = !this.isShowReuseOrder

      this.$store.dispatch('setActiveOrder', null)
    },
    handleReuseOrderSuccess() {
      if (this.isBigC) {
        this.refreshData()
      }
    }
  },
  async created() {
    await this.$_checkAnCuongRecordsV2Permission()
    this.refreshData()
  },
  beforeMount() {
    const userProfile = this.$store.getters.userProfile
    if (get(userProfile, 'partner') == this.$constants.HARDCODED_PARTNER.COOP_MART) {
      const isChildAccount = get(userProfile, 'parent_id')
      if (isChildAccount) {
        const saasPermissions = get(userProfile, 'saas_permissions') || []
        if (!saasPermissions.includes('BOOKING')) {
          // this.$router.push({ name: 'profile' })
          this.showDialogPermission = true
        }
      }
    }
    // const defaultFilter = this.$store.getters.ordersDefaultFilter
    // this.params.from_time = defaultFilter.fromDate
    // this.params.to_time = defaultFilter.toDate
    // this.getOrdersMeta()
    this.$store.dispatch('getEmployees')
  },
  mounted() {
    if (this.isMobile) {
      this.$_preventScroll(true)
    }
  },
  beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }
  },
  watch: {
    autoRefresh(val) {
      if (val) {
        this.intervalId = setInterval(() => {
          if (this.isFocusTab) {
            this.refreshData()
          }
        }, this.intervalDuration)
      } else {
        if (this.intervalId) {
          clearInterval(this.intervalId)
        }
      }
    },
    activeOrder(newVal, oldVal) {
      if (newVal !== oldVal) {
        // console.log(newVal)
        this.activeOrderData = newVal
        const service = this.services && this.services.find((e) => e._id === newVal.service_id)
        this.activeOrderData.service = service
      }
    }
  }
}
</script>

<i18n src="@/locales2/share/datetime.json"></i18n>

<style lang="scss" scoped>
$records-histoty__header__height: 110px;
$records-history__body__tool__height: 76px;
$records-history__pagination__height: 56px;

.records-history {
  height: 100vh;

  &.records-history-mobile {
    background: #f2f2f2;
    height: stretch;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
  }

  .records-history__header {
    background-color: #fff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);

    .records-history__block {
      display: flex;
      align-items: center;
    }

    .records-history__header-wrapper {
      padding: 16px 20px 4px;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .records__tool-filter {
        margin-left: auto;

        .records-refresh__btn {
          border-radius: 41px;
          text-transform: none;
          font-weight: normal;

          .tag-icon {
            margin-right: 4px;
          }
        }
      }

      &.records-history__header-mobile {
        padding: 16px 12px 12px 0;

        .records-history__header__title {
          font-size: 16px;
        }
      }

      .records-history__mobile-filter-btn {
        margin: 0;
        width: 36px;
        min-width: 36px;
        height: 36px;
        border-radius: 8px;

        &.filter-btn {
          margin-left: auto;
          margin-right: 12px;
        }
      }

      .records-history__sidebar-icon {
        margin: 0 12px;
      }

      .records-history__header__title {
        font-size: 24px;
        font-weight: 500;
      }

      .records-history__export-data {
        margin-left: 30px;
      }

      .timeline-icon {
        margin-left: 4px;
      }
    }

    .records-history-tabs {
      padding: 0 4px;
    }
  }

  .records-history__body {
    padding: 25px;
    height: calc(100% - #{$records-histoty__header__height});
    background: #f2f2f2;

    .records-history__card {
      height: 100%;
      background-color: #fff;
      border-radius: 4px;

      .records-history__body__tool {
        padding: 25px;
        display: flex;
        align-items: center;
        flex-wrap: wrap;

        #recordsSearchBox {
          min-width: 400px;
          margin-right: 20px;
        }
      }

      .records-history__body__list {
        height: calc(
          100% - #{$records-history__body__tool__height} - #{$records-history__pagination__height}
        );

        .records-history__body__list-data {
          height: 100%;
        }

        .records-history__body__list-empty {
          padding: 12px 20px 28px 20px;
          color: #7a8189;
          font-size: 16px;
        }

        .records-history__body__list-no-result {
          display: flex;
          flex-direction: column;
          text-align: center;

          img {
            width: 380px;
            height: 300px;
            margin: 0 auto;
            padding-left: 25px;
          }

          span {
            color: #7a8189;
          }
        }

        .records-history__body__list__footer {
          height: 56px;
          display: flex;
          justify-content: flex-end;
          align-items: center;
          padding: 0 25px;
          background: #fff;
          border-top: 1px solid #e8e8e8;
          border-radius: 0 0 4px 4px;

          .records-pagination {
            display: flex;
            align-items: center;

            .records-pagination-actions {
              button {
                min-width: 34px !important;
                height: 34px;
                padding: 0;
                margin: 6px 4px;
                border-radius: 6px;
              }
            }

            .records-pagination-info {
              padding-right: 8px;
              font-size: 16px;
              color: rgba(0, 0, 0, 0.54);

              .records-pagination-range:hover {
                cursor: pointer;
                text-decoration: underline;
              }
            }
          }
        }
      }
    }

    .records-history__loading {
      text-align: center;
      padding: 20px 0;

      span {
        margin-left: 10px;
        color: #ff8200;
        font-size: 13px;
      }
    }
  }

  .records-history__body.full-height {
    height: 100%;
  }

  .records-history__timeline {
    margin-left: auto;
  }
}

.records-pagination-options {
  .v-list {
    padding: 4px 0;

    > span {
      padding: 0 16px;
    }
  }
}

@media screen and (max-width: 480px) {
  $records-histoty__header__height: 106px;

  .records-history {
    .records-history__body {
      padding: 0;
      height: calc(100% - #{$records-histoty__header__height});

      .records-history__card {
        background-color: initial;
        display: flex;
        flex-direction: column;

        .records-history__body__tool {
          padding: 12px;
          margin: 8px 0;
          background-color: #fff;

          #recordsSearchBox {
            min-width: 100%;
            margin-right: 0;
          }
        }

        .records-history__body__list {
          height: inherit;
          overflow-y: hidden;

          .records-history__body__list-no-result {
            img {
              padding-left: 0;
            }
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
.records-history__timeline-list {
  height: 70vh;
  width: 400px;
}

.records-history__body__tool {
  .order-tag {
    .order-tag__text {
      button {
        margin: 0;
      }
    }
  }
}

.records-history__mobile-filter-btn {
  &.filter-search {
    svg {
      path {
        fill: #1a1a1a;
      }
    }
  }
}

.v-list--two-line .v-list__tile {
  height: 60px;
}
</style>
